/**
 * Advance widths of Public Sans, in thousandths of an em, measured in Chrome
 * for printable ASCII and the punctuation proposals use, at the four weights
 * the pages set (400 regular, 600 semibold, 700 bold, 900 black).
 *
 * The pages are paginated before any browser lays them out (the same HTML is
 * printed, previewed in the studio and shown to the client), so line counts
 * are worked out here: words are wrapped greedily against these widths. A
 * character not in the table counts as a wide one.
 */

const CHARS = " !\"#$%&'()*+,-./0123456789:;<=>?@ABCDEFGHIJKLMNOPQRSTUVWXYZ[\\]^_`abcdefghijklmnopqrstuvwxyz{|}~’‘“”–—·é×−…"
const WIDTHS = {
  r: [243,251,425,603,658,904,708,248,303,309,394,555,287,377,271,351,606,407,596,624,628,640,617,612,642,618,225,240,546,555,545,521,831,718,676,689,693,627,594,733,766,283,388,682,577,885,754,731,636,745,676,676,618,701,682,963,734,647,650,303,351,303,473,563,218,551,597,540,597,567,374,611,577,253,262,562,289,865,577,565,596,596,391,504,345,581,514,789,556,544,490,328,263,332,568,231,239,419,410,555,1117,276,567,540,555,814],
  s: [233,268,455,610,671,923,749,255,319,324,437,573,299,386,287,367,626,418,617,645,644,659,639,623,656,639,250,261,563,573,563,528,829,731,688,705,708,633,605,747,768,296,396,701,578,903,769,744,645,757,687,689,633,713,704,976,745,676,668,322,367,322,479,575,243,558,603,543,603,572,382,617,585,262,277,571,304,882,586,571,602,602,402,515,367,589,528,799,571,565,501,359,266,360,592,248,252,452,447,575,1134,284,572,560,573,875],
  b: [226,282,480,616,681,939,784,261,333,336,473,588,308,393,301,381,642,428,635,662,656,675,657,632,668,656,270,278,577,588,578,535,827,742,697,718,720,638,614,759,770,307,402,717,578,918,782,754,652,767,696,700,647,724,722,987,753,701,683,337,381,337,484,584,264,563,608,546,608,576,388,623,592,270,288,580,315,896,593,576,607,607,412,524,386,595,539,807,584,582,510,384,268,384,612,262,263,479,478,592,1149,290,576,577,588,926],
  k: [210,310,529,628,701,971,852,273,361,361,545,619,327,407,328,409,675,448,670,697,682,706,695,650,692,690,310,313,606,619,607,547,824,764,716,745,744,648,632,783,774,330,414,749,578,947,808,776,667,788,714,721,673,746,759,1009,770,749,713,368,409,368,494,603,305,574,619,551,619,585,402,634,607,285,312,596,339,924,608,585,618,618,432,542,423,609,562,824,609,616,529,436,273,431,652,289,284,534,539,626,1178,304,585,610,619,1028],
}
const INDEX = new Map([...CHARS].map((c, i) => [c, i]))

/** Width in px of a run of text at a size and weight ('r', 's', 'b', 'k'). */
export function textWidth(str, size, weight = 'r', tracking = 0) {
  const table = WIDTHS[weight] || WIDTHS.r
  let units = 0
  for (const ch of String(str ?? '')) {
    const i = INDEX.get(ch)
    units += (i === undefined ? 700 : table[i]) + tracking * 1000
  }
  return (units / 1000) * size
}

/**
 * Lines a text takes when wrapped at `width`, word by word, the way the
 * browser wraps it. Hard line breaks start a new line. `tracking` is the
 * letter-spacing in em. The table leaves kerning out, which only ever makes
 * a line look longer than it is, so the width is used nearly whole.
 */
export function wrapCount(str, size, width, weight = 'r', tracking = 0) {
  const t = String(str ?? '').trim()
  if (!t) return 0
  const room = width
  const space = textWidth(' ', size, weight, tracking)
  let lines = 0
  for (const para of t.split('\n')) {
    let line = 0
    lines += 1
    for (const word of para.split(/\s+/).filter(Boolean)) {
      const ww = textWidth(word, size, weight, tracking)
      if (line === 0) { line = ww; if (ww > room) lines += Math.floor(ww / room) }
      else if (line + space + ww <= room) line += space + ww
      else { lines += 1; line = ww; if (ww > room) lines += Math.floor(ww / room) }
    }
  }
  return lines
}
